import * as React from "react";

import { Box, Button, Container, Backdrop } from "@mui/material";
import useScreenSize, { useScreenChecks } from "@/hooks/useScreenSize";
import SpeedDial from '@mui/material/SpeedDial';
// import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
// import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Drawer from '@mui/material/Drawer';
import type { Breakpoint } from "@mui/system";

import DotaSelector from "@ds-components/Main/dotaselector";
import SpotifyLogin from "@ds-components/Main/spotifylogin";
import Credits from "@ds-components/Main/credits";
import SpotifyTing from "@ds-components/Playlist/playlist";
import { PlaylistPlayer } from "@ds-components/Playlist/playlistplayer";
import { getLocalStorage } from "@ds-services/helpers";
import { dotaSpottoTheme, heroSelectorStyles } from "@ds-components/General/dotaspottotheme";
import "../../scss/heroselect.scss";
// import "@ds-scss/heroselect.scss";

export interface PlaylistStatus {
	status?: boolean,
	visible?: boolean,
	loading?: boolean
}
export interface StoredPlaylists {
	description?: string,
	id?: string,
	name?: string,
	uri?: string,
	image?: string,
	url: string,
	hero?: {
		avatar: string,
		video: string
	},
}

const HeroSelect: React.FC<{}> = () => {
	// Use custom hooks for screen size detection
	const screenSize = useScreenSize();
	useScreenChecks(); // We're not using the return values yet, but keeping for potential future use
	const [screenSizes, setScreenSizes] = React.useState<Breakpoint | null>(screenSize);

	// Update screenSizes state when screenSize changes
	React.useEffect(() => {
		setScreenSizes(screenSize);
	}, [screenSize]);
	const [playlistLoading, setPlaylistLoading] = React.useState<boolean>(false);
	const [thehero, setTheHero] = React.useState<any | null>({
		status: "loading",
	});
	const sendDataToGrandParent = React.useCallback((index: any) => {
		setTheHero({
			status: "loaded",
			payload: index,
			action: index.action,
		});
	}, []);

	//Most Recent Playlist Available
	const [playlistVisible, setPlaylistVisible] = React.useState<PlaylistStatus>({
		status: false,
		visible: false,
		loading: false,
	});
	const checkPlaylistVisible = (show?: PlaylistStatus) => {
		if (show) {
			if (!show.loading) {
				setPlaylistVisible(show);
				if (playlistLoading) {
					setPlaylistLoading(false);
				}
			} else {
				if (show.loading && !playlistLoading) {
					setPlaylistLoading(true);
				}
			}
		} else {
			setPlaylistVisible({
				status: false,
				visible: false,
				loading: false,
			});
		}
		return playlistVisible;
	};


	//Previous Playlists
	const getSavedPlaylists = getLocalStorage('saved_playlists');
	const formatSavedPlaylists = getSavedPlaylists ? JSON.parse(getSavedPlaylists) as StoredPlaylists[] : null;
	const [playlistPlayerOpen, setPlaylistPlayerOpen] = React.useState<boolean>(!formatSavedPlaylists);

	//Playlist Controller
	const [playlistControllerOpen, setPlaylistControllerOpen] = React.useState(false);


	const toggleStates = {
		open: {
			playlistDial() {
				setPlaylistControllerOpen(true)
			},
			playlistPlayer() {
				setPlaylistPlayerOpen(true)
			}
		},
		close: {
			playlistDial() {
				setPlaylistControllerOpen(false)
			},
			playlistPlayer() {
				setPlaylistPlayerOpen(false)
			}
		}
	}
	// Update screenSizes when screenSize changes
	React.useEffect(() => {
		setScreenSizes(screenSize);
	}, [screenSize]);


	return (
		<Box id="heroSelect"
			sx={heroSelectorStyles}
			className={thehero.payload ? thehero.payload : ""}
		>
			<SpotifyLogin/>
			<Box sx={{
				display: 'flex',
				justifyContent: 'center',
				overflowX: "hidden"
			}}>
				<Container sx={{}}>
					<Box
						sx={{
							paddingTop: 0,
							paddingBottom: 1,
						}}>
						<h1 style={{
							textAlign: "center",
							fontSize: "3rem",
							fontWeight: "bold",
						}}>
							Dota Tunes
						</h1>
					</Box>
					<DotaSelector sendDataToGrandParent={sendDataToGrandParent} playlistloading={playlistLoading} />
					{
						((!playlistVisible.visible && playlistVisible.status) || formatSavedPlaylists) && (
							<>
								<Backdrop open={playlistControllerOpen} />
								<SpeedDial
									id="userPlaylistsContainer"
									ariaLabel="Playlist Controller"
									sx={{
										position: 'fixed',
										bottom: 16,
										right: 16,
										"& .MuiSpeedDialAction-staticTooltipLabel": {
											whiteSpace: "nowrap",
											backgroundColor: "gold",
											color: 'black',
										},
										"& .MuiSpeedDialAction-fab": {
											backgroundColor: "black",
											fontSize: '2.21rem',
											border: '1px solid gold',
											overflow: "hidden",
										},
									}}
									icon={"⚔️"}
									onClose={() => toggleStates.close.playlistDial()}
									onOpen={() => toggleStates.open.playlistDial()}
									open={playlistControllerOpen}
								>
									{(!playlistVisible.visible && playlistVisible.status) ? (
										<SpeedDialAction
											key={"most-recent-playlist"}
											icon={"🎧"}
											title={"Most Recent Playlist"}
											onClick={() => {
												setPlaylistVisible({
													visible: true,
												});
												toggleStates.close.playlistDial()
											}}
											slotProps={
												{
													tooltip: {
														title: "Most Recent Playlist",
														placement: "right-start",
														open: true // Consider if this should always be true or depend on SpeedDial state
													}
												}
											}
										/>
									) : null /* Return null explicitly */}

									{formatSavedPlaylists ? (
										<SpeedDialAction
											key={"play-playlist"}
											icon={"📻"}
											title={"Play Playlist"}
											onClick={() => {
												toggleStates.open.playlistPlayer()
												toggleStates.close.playlistDial()
											}}
											slotProps={
												{
													tooltip: {
														title: "Playlist Player",
														placement: "right-start",
														open: true // Consider if this should always be true or depend on SpeedDial state
													}
												}
											}
										/>
									) : null /* Return null explicitly */}
								</SpeedDial>
							</>
						)
					}
				</Container>
				{thehero.status === "loaded" && thehero.action === "search" && (
					<SpotifyTing thehero={thehero} checkPlaylistVisible={checkPlaylistVisible} seePlaylistStatus={playlistVisible} userScreenSize={screenSizes} />
				)}
				{formatSavedPlaylists && playlistPlayerOpen && (
					<Drawer
						anchor="bottom"
						open={playlistPlayerOpen}
						onClose={() => {
							toggleStates.close.playlistPlayer()
						}}
						// swipeAreaWidth={drawerBleeding}
						// disableSwipeToOpen={true}
						keepMounted
					>
						<Container
							maxWidth={false}
							sx={{
								maxWidth: "100% !important",
								width: "100%",
								backgroundColor: "gold",
								display: "flex",
								py: 1.5,
							}}
						>
							<Button
								variant="contained"
								sx={{
									marginLeft: "auto",
								}}
								onClick={() => toggleStates.close.playlistPlayer()}
							>
								Close Playlist Player
							</Button>
						</Container>
						<PlaylistPlayer playlists={formatSavedPlaylists} />
					</Drawer>
				)}
			</Box>
			<Credits />
		</Box>
	);
};
export default HeroSelect;
